import React, { useState, useEffect } from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import { capitalize } from 'underscore.string';

import {
  Editor,
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
} from 'draft-js';

import LayoutContext from '../../../../store/Layout/contexts';
import Upload from '../../../../components/Input/Upload';
import WYSIWYG from '../../../../components/Input/WYSIWYG';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';
import AlertService from '../../../../components/SweetAlert';
import { newFetch, POST } from '../../../../helpers/fetch';

import AddPreview from './Preview';

const AddItem = ({ history }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState(EditorState.createEmpty());
  const types = ['free', 'redeemable'];
  const [type, setType] = useState('free');
  const [threshold, setThreshold] = useState(0);
  const [points, setPoints] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [mailingPoints, setTMailingPoints] = useState(0);
  const [images, setImages] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [collectionPoint, setCollectionPoint] = useState('');
  const [remarks, setRemarks] = useState('');

  const [isPreview, setIsPreview] = useState(false);

  const isJSON = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const handleDiscard = async () => {
    if (
      await AlertService.confirm(
        '',
        'Are you sure you want to discard changes?'
      )
    ) {
      setName('');
      setDescription('');
      setType('free');
      setThreshold(0);
      setPoints(0);
      setQuantity(0);
      setImages([]);
      setInstructions('');
      setCollectionPoint('');
      setRemarks('');
    }
  };

  const handleValidation = () => {
    if (!name) {
      ToastUtil.error('Please input Item Name!');
      return false;
    }

    if (type === 'free' && threshold === 0) {
      ToastUtil.error('Please input Item Threshold!');
      return false;
    }

    if (type === 'redeemable' && points === 0) {
      ToastUtil.error('Please input Item Points!');
      return false;
    }

    if (type === 'redeemable' && quantity === 0) {
      ToastUtil.error('Please input Item Quantity!');
      return false;
    }

    if (images.length === 0) {
      ToastUtil.error('Please add Image to Item!');
      return false;
    }

    if (!instructions) {
      ToastUtil.error('Please add Instructions to redeem Item!');
      return false;
    }

    if (!collectionPoint) {
      ToastUtil.error('Please add Collection Point to Item!');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      handleValidation() &&
      (await AlertService.confirm(
        '',
        'Are you sure you want to add this item?'
      ))
    ) {
      let api_path = `/reward`;
      let response = await newFetch(POST, api_path, {
        name,
        description: JSON.stringify(
          convertToRaw(description.getCurrentContent())
        ),
        type,
        free_threshold: threshold,
        redeem_points: points,
        mailing_cost: mailingPoints,
        quantity,
        instructions,
        collection_point: collectionPoint,
        remarks,
      });

      if (response.success) {
        let item = response.results;

        let formData = new FormData();
        images.map((image) => formData.append('files[]', image));

        let upload_path = `/reward/${item.id}/upload/image`;
        let upload_response = await newFetch(
          POST,
          upload_path,
          formData,
          'form'
        );

        if (upload_response.success) {
          ToastUtil.success(response.message);
          history.push('/rewards');
        } else {
          ToastUtil.error(response.message);
        }
      }
    }
  };

  useEffect(() => {
    setPoints(0);
    setThreshold(0);
  }, [type]);

  return isPreview ? (
    <AddPreview
      setIsPreview={setIsPreview}
      handleSubmit={handleSubmit}
      name={name}
      description={description}
      type={type}
      points={points}
      quantity={quantity}
      mailingPoints={mailingPoints}
      images={images}
      instructions={instructions}
      collectionPoint={collectionPoint}
      remarks={remarks}
    />
  ) : (
    <LayoutContext.Consumer>
      {({ activeSidebar }) => {
        return (
          <div style={{ paddingTop: '3rem' }}>
            <div
              className="d-flex flex-column"
              style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
            >
              <NavLink
                to="/rewards"
                className="text-decoration-none"
                style={{ color: 'inherit' }}
              >
                <div style={{ color: '#5156BE' }}>
                  <i className="fas fa-chevron-left pe-2"></i>Back to Rewards
                </div>
              </NavLink>
              <div className="pt-4">
                <h5>Add New Item</h5>
              </div>
            </div>
            {/* <form className="form" onSubmit={e => handleSubmit(e)}> */}
            <div
              className="border rounded d-flex"
              style={{
                marginTop: '2rem',
                marginLeft: '3rem',
                marginRight: '3rem',
              }}
            >
              <div
                style={{
                  minWidth: '15rem',
                  width: '15rem',
                  backgroundColor: '#f3f3f3',
                  padding: '2.25rem',
                }}
                className="border-right"
              >
                <strong>Item Information</strong>
                <p className="pt-2 text-muted">Manage item's information</p>
              </div>

              <div
                className="flex-grow-1 bg-white"
                style={{ padding: '2.25rem' }}
              >
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="itemType">
                      <b>Type of Reward</b>
                    </label>
                    <div className="d-flex mt-2">
                      {types.map((t, index) => (
                        <div key={index} className="form-check me-3">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="itemType"
                            id={`itemType-${index}`}
                            value={t}
                            checked={t === type}
                            onChange={({ target: { value } }) => setType(value)}
                          />
                          <label
                            className="form-check-label ms-1 pt-1"
                            htmlFor={`itemType-${index}`}
                          >
                            {`${capitalize(t)} Item`}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-sm-8">
                    <label htmlFor="itemName">
                      <b>Item Name*</b>
                    </label>
                    <input
                      type="text"
                      className="form-control mt-3"
                      placeholder="Item Name"
                      id="itemName"
                      name="itemName"
                      value={name}
                      onChange={({ target: { value } }) => setName(value)}
                    />
                  </div>

                  <div className="col-sm-4">
                    <label htmlFor="itemPoints">
                      <b>Points to Redeem*</b>
                    </label>
                    <input
                      type="number"
                      className="form-control mt-3 text-end"
                      id="itemPoints"
                      name="itemPoints"
                      min="0"
                      value={points ? points : 0}
                      disabled={type === 'free'}
                      onChange={({ target: { value } }) =>
                        setPoints(parseInt(value))
                      }
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-sm-4">
                    <label htmlFor="itemQuantity">
                      <b>Quantity*</b>
                    </label>
                    <input
                      type="number"
                      className="form-control mt-3 text-end"
                      id="itemQuantity"
                      name="itemQuantity"
                      min="0"
                      value={quantity ? quantity : 0}
                      onChange={({ target: { value } }) =>
                        setQuantity(parseInt(value))
                      }
                    />
                  </div>
                  <div className="col-sm-4">
                    <label htmlFor="itemThreshold">
                      <b>Item Threshold*</b>
                    </label>
                    <input
                      type="number"
                      className="form-control mt-3 text-end"
                      id="itemThreshold"
                      name="itemThreshold"
                      value={threshold ? threshold : 0}
                      min="0"
                      onChange={({ target: { value } }) =>
                        setThreshold(parseInt(value))
                      }
                    />
                  </div>
                  <div className="col-sm-4">
                    <label htmlFor="itemThreshold">
                      <b>Mailing Points*</b>
                    </label>
                    <input
                      type="number"
                      className="form-control mt-3 text-end"
                      id="itemThreshold"
                      name="itemThreshold"
                      value={mailingPoints ? mailingPoints : 0}
                      min="0"
                      onChange={({ target: { value } }) =>
                        setTMailingPoints(parseInt(value))
                      }
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-sm-12">
                    <label htmlFor="itemDescription" className="mb-3">
                      <b>Item Description</b>
                    </label>
                    <WYSIWYG state={description} setState={setDescription} />
                    {/* <textarea
                      className="form-control mt-3"
                      rows="5"
                      style={{ resize: 'none' }}
                      placeholder="Item Description"
                      id="itemDescription"
                      name="itemDescription"
                      value={description}
                      onChange={({ target: { value } }) =>
                        setDescription(value)
                      }
                    /> */}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="border rounded d-flex"
              style={{
                marginTop: '2rem',
                marginLeft: '3rem',
                marginRight: '3rem',
              }}
            >
              <div
                style={{
                  minWidth: '15rem',
                  width: '15rem',
                  backgroundColor: '#f3f3f3',
                  padding: '2.25rem',
                }}
                className="border-right"
              >
                <strong>Images</strong>
                <p className="pt-2 text-muted">Manage images of item</p>
              </div>

              <div
                className="flex-grow-1 bg-white"
                style={{ padding: '2.25rem' }}
              >
                <Upload files={images} setFiles={setImages} />
              </div>
            </div>
            <div
              className="border rounded d-flex"
              style={{
                marginTop: '2rem',
                marginLeft: '3rem',
                marginRight: '3rem',
              }}
            >
              <div
                style={{
                  minWidth: '15rem',
                  width: '15rem',
                  backgroundColor: '#f3f3f3',
                  padding: '2.25rem',
                }}
                className="border-right"
              >
                <strong>Instructions</strong>
                <p className="pt-2 text-muted">How to redeem item</p>
              </div>

              <div
                className="flex-grow-1 bg-white"
                style={{ padding: '2.25rem' }}
              >
                <textarea
                  className="form-control adjust-fs"
                  rows="5"
                  style={{ resize: 'none' }}
                  placeholder="Item Instruction"
                  id="itemInstruction"
                  name="itemInstruction"
                  value={instructions}
                  onChange={({ target: { value } }) => setInstructions(value)}
                />
              </div>
            </div>
            <div
              className="border rounded d-flex"
              style={{
                marginTop: '2rem',
                marginLeft: '3rem',
                marginRight: '3rem',
              }}
            >
              <div
                style={{
                  minWidth: '15rem',
                  width: '15rem',
                  backgroundColor: '#f3f3f3',
                  padding: '2.25rem',
                }}
                className="border-right"
              >
                <strong>Collection Point</strong>
                <p className="pt-2 text-muted">Where to collect item</p>
              </div>
              <div
                className="flex-grow-1 bg-white"
                style={{ padding: '2.25rem' }}
              >
                <textarea
                  className="form-control adjust-fs"
                  rows="5"
                  style={{ resize: 'none' }}
                  placeholder="Item Collection Point"
                  id="itemCollectionPoint"
                  name="itemCollectionPoint"
                  value={collectionPoint}
                  onChange={({ target: { value } }) =>
                    setCollectionPoint(value)
                  }
                />
              </div>
            </div>
            <div
              className="border rounded d-flex"
              style={{
                marginTop: '2rem',
                marginLeft: '3rem',
                marginRight: '3rem',
                marginBottom: '6rem',
              }}
            >
              <div
                style={{
                  minWidth: '15rem',
                  width: '15rem',
                  backgroundColor: '#f3f3f3',
                  padding: '2.25rem',
                }}
                className="border-right"
              >
                <strong>Remarks</strong>
                <p className="pt-2 text-muted">
                  Additional information (optional)
                </p>
              </div>
              <div
                className="flex-grow-1 bg-white"
                style={{ padding: '2.25rem' }}
              >
                <textarea
                  className="form-control adjust-fs"
                  rows="5"
                  style={{ resize: 'none' }}
                  placeholder="Item Remarks"
                  id="itemRemarks"
                  name="itemRemarks"
                  value={remarks}
                  onChange={({ target: { value } }) => setRemarks(value)}
                />
              </div>
            </div>
            <div
              style={{
                position: 'fixed',
                bottom: 0,
                width: activeSidebar ? 'calc(100vw - 310px)' : '100vw',
                height: '4.5rem',
                paddingRight: '3rem',
              }}
              className="border-top bg-white d-flex align-items-center justify-content-end"
            >
              <button
                type="button"
                className="btn btn-outline-brand adjust-fs adjust-shadow"
                style={{ width: '12rem' }}
                onClick={handleDiscard}
              >
                Discard changes
              </button>
              <button
                type="button"
                className="btn btn-brand ms-3 adjust-fs adjust-shadow"
                style={{ width: '12rem' }}
                onClick={async () =>
                  handleValidation() &&
                  (await AlertService.confirm(
                    '',
                    'Are you sure you want to proceed to the Preview Page?'
                  )) &&
                  setIsPreview(true)
                }
              >
                Preview
              </button>
            </div>
            {/* </form> */}
          </div>
        );
      }}
    </LayoutContext.Consumer>
  );
};

export default withRouter(AddItem);
